<script setup lang="ts">
import { computed } from 'vue';
import type { CustomerDownloadPageStatus } from './customerPortraitDownloadTypes';

interface Props {
  compact?: boolean;
  status: CustomerDownloadPageStatus | 'partial';
}

interface Emits {
  enterCode: [];
  retry: [];
}

const props = withDefaults(defineProps<Props>(), {
  compact: false,
});
const emit = defineEmits<Emits>();

const messageKey = computed(() => {
  switch (props.status) {
    case 'loading':
      return 'CustomerPortraitsDownloadCode.placeholder';
    case 'invalid':
      return 'CustomerPortraitsDownload.errors.wrongFormat';
    case 'not-found':
      return 'CustomerPortraitsDownload.errors.notFound';
    case 'processing':
      return 'CustomerPortraitsDownloadCode.states.processing';
    case 'empty':
      return 'errors.portraits_download_portraits_error';
    case 'partial':
      return 'CustomerPortraitsDownloadCode.states.partial';
    default:
      return 'CustomerPortraitsDownload.errors.serverError';
  }
});

const canRetry = computed(() =>
  ['empty', 'processing', 'server-error'].includes(props.status)
);
const canEnterCode = computed(() =>
  ['empty', 'invalid', 'not-found', 'server-error'].includes(props.status)
);
const showsSupport = computed(
  () => !['loading', 'partial', 'processing'].includes(props.status)
);
const statusRole = computed(() =>
  ['loading', 'partial', 'processing'].includes(props.status)
    ? 'status'
    : 'alert'
);
</script>

<template>
  <section
    class="CustomerDownloadStatus"
    :class="{ 'CustomerDownloadStatus--compact': compact }"
    :style="
      compact
        ? {
            '--CustomerDownloadStatus--surface-background':
              'var(--Kiosk--popup_background_color)',
            '--CustomerDownloadStatus--surface-color':
              'var(--Kiosk--popup_font_color)',
          }
        : undefined
    "
    :aria-busy="status === 'loading'"
    :role="statusRole"
    data-testid="customer-download-status"
  >
    <span
      v-if="status === 'loading'"
      class="CustomerDownloadStatus__spinner"
      aria-hidden="true"
    />
    <p class="CustomerDownloadStatus__message">{{ $t(messageKey) }}</p>

    <div v-if="canRetry || canEnterCode" class="CustomerDownloadStatus__actions">
      <button
        v-if="canRetry"
        class="CustomerDownloadStatus__button kiosk-button"
        data-testid="customer-download-retry"
        type="button"
        @click="emit('retry')"
      >
        {{ $t('CustomerPortraitsDownloadCode.buttonRetry') }}
      </button>
      <button
        v-if="canEnterCode"
        class="CustomerDownloadStatus__button kiosk-button-inline"
        data-testid="customer-download-enter-code"
        type="button"
        @click="emit('enterCode')"
      >
        {{ $t('CustomerPortraitsDownloadCode.actions.enterAnotherCode') }}
      </button>
    </div>

    <a
      v-if="showsSupport"
      class="CustomerDownloadStatus__support"
      href="mailto:service@theartisan.ai"
    >
      {{ $t('CustomerPortraitsDownloadCode.actions.contactSupport') }}
    </a>
  </section>
</template>

<style scoped>
.CustomerDownloadStatus {
  display: grid;
  justify-items: center;
  justify-self: center;
  align-content: center;
  gap: 1.5rem;
  width: min(42rem, 100%);
  min-height: min(24rem, 70vh);
  margin: auto;
  padding: clamp(2rem, 7vw, 5rem);
  border: 1px solid rgba(var(--Kiosk--font_color-rgb), 0.16);
  border-radius: 1.5rem;
  color: var(
    --CustomerDownloadStatus--surface-color,
    var(--Kiosk--font_color)
  );
  background: var(
    --CustomerDownloadStatus--surface-background,
    rgba(var(--Kiosk--background_color-rgb), 0.88)
  );
  box-shadow: 0 1.5rem 4rem rgba(var(--Kiosk--tint-color-rgb), 0.12);
  text-align: center;
}

.CustomerDownloadStatus--compact {
  display: flex;
  justify-content: center;
  width: min(62rem, 100%);
  min-height: 0;
  margin: 0 auto 1.5rem;
  padding: 0.9rem 1.25rem;
  border-radius: 0.8rem;
  box-shadow: none;
}

.CustomerDownloadStatus__message {
  max-width: 34rem;
  font-size: clamp(1rem, 2vw, 1.45rem);
  line-height: 1.5;
}

.CustomerDownloadStatus--compact .CustomerDownloadStatus__message {
  max-width: none;
  font-size: clamp(0.9rem, 1.5vw, 1.1rem);
}

.CustomerDownloadStatus__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.9rem;
}

.CustomerDownloadStatus__button {
  min-width: 11rem;
}

.CustomerDownloadStatus__support {
  font-size: clamp(0.85rem, 1.4vw, 1rem);
}

.CustomerDownloadStatus__spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 0.22rem solid rgba(var(--Kiosk--font_color-rgb), 0.18);
  border-top-color: var(--Kiosk--font_color);
  border-radius: 50%;
  animation: CustomerDownloadStatus-spin 0.8s linear infinite;
}

@keyframes CustomerDownloadStatus-spin {
  to {
    transform: rotate(1turn);
  }
}

@media (max-width: 42rem) {
  .CustomerDownloadStatus:not(.CustomerDownloadStatus--compact) {
    min-height: min(22rem, 68vh);
    border-radius: 1rem;
  }

  .CustomerDownloadStatus__actions {
    width: 100%;
  }

  .CustomerDownloadStatus__button {
    width: 100%;
  }
}
</style>
